import { type ReactNode } from 'react'
import { Sandwich, SandwichBun, SandwichContent } from '@/components/Sandwich'
import { ScrollView } from '@/components/ScrollView'
import { Avatar } from '@/components/Avatar'
import { QuestionIcon } from '@/features/jobs/questions/components/QuestionIcon'
import { CompletionToggle } from '@/components/CompletionToggle'
import { Text } from '@/components/Text'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { isNotNil } from '@/utils/object-util'
import { match } from '@/utils/pattern-match-util'
import {
  SplitViewHeader,
  SplitViewHeaderTitle,
} from '@/components/SplitView/SplitViewHeader'
import {
  SplitViewSidebarListItem,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemSubtitle,
  SplitViewSidebarListItemTitle,
} from '@/components/SplitView/SplitViewSidebarListItem'
import type { OutlineItemDisplayVisual } from '@/features/jobs/drafting/OutlineViewModel'

export interface OutlineProps {
  title: string
  children?: ReactNode
}

export function Outline({ title, children }: OutlineProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()
  return (
    <Sandwich stretch>
      <SandwichBun>
        <SplitViewHeader compact scrollOffset={scrollOffset}>
          <SplitViewHeaderTitle testId="draft-title" title={title}>
            {title}
          </SplitViewHeaderTitle>
        </SplitViewHeader>
      </SandwichBun>
      <SandwichContent>
        <ScrollView ref={scrollRef}>{children}</ScrollView>
      </SandwichContent>
    </Sandwich>
  )
}

export interface OutlineItemProps {
  stepId: string
  title: string
  value?: string | null
  shortValue?: string | null
  visual?: OutlineItemDisplayVisual | null
  isActive: boolean
  pulse: boolean
  onPress: () => unknown
}

const VISUAL_SIZE = 32

export function OutlineItem({
  stepId,
  title,
  value,
  shortValue,
  visual,
  isActive,
  pulse,
  onPress,
}: OutlineItemProps) {
  const stepVisual =
    visual &&
    match(visual, 'type', {
      Image: (v) => <Avatar size={VISUAL_SIZE} subject={{ avatar: v.url }} />,
      QuestionIcon: (v) => (
        <QuestionIcon size={VISUAL_SIZE} type={v.questionType} />
      ),
      EmptyAvatar: () => <Avatar size={VISUAL_SIZE} subject={null} />,
    })

  return (
    <SplitViewSidebarListItem
      active={isActive}
      testId={`outline-item-${stepId}`}
      aria-current={isActive ? 'step' : undefined}
      extras={<SplitViewSidebarListItemArrow />}
      onPress={onPress}
      visual={
        <CompletionToggle
          size={VISUAL_SIZE}
          completed={isNotNil(stepVisual)}
          readOnly
          pulse={pulse}
        >
          {stepVisual}
        </CompletionToggle>
      }
    >
      <SplitViewSidebarListItemTitle
        wrap
        title={title}
        data-test={`outline-item-title-${stepId}`}
      >
        {title}
      </SplitViewSidebarListItemTitle>
      {value && (
        <SplitViewSidebarListItemSubtitle
          wrap
          title={value}
          testId={`outline-item-value-${stepId}`}
        >
          <Text text={shortValue || value} />
        </SplitViewSidebarListItemSubtitle>
      )}
    </SplitViewSidebarListItem>
  )
}
